import { useState, useEffect, useRef } from 'react';
import { getVideoStreamUrl } from '../../services/videoApi';
import { VideoDetailPlayerSectionProps, UseVideoDetailPlayerSectionReturn } from './types';
import { BREAKPOINTS } from '../../config/breakpoints';

const FINAL_RETRY_MAX = 1;

// ReactPlayer ラッパーのインターフェース（VideoJS の VideoJSPlayer とは別）
interface ReactPlayerInstance {
  seekTo?: (seconds: number) => void;
  getInternalPlayer?: () => { vjsPlayer?: { el: () => HTMLElement | null } } | null;
  [key: string]: unknown;
}

// VideoJS Player のうち音声付き自動再生の制御に使う API のみの最小型
interface VjsPlayerLike {
  muted: (value: boolean) => void;
  play: () => Promise<void> | undefined;
  isDisposed?: () => boolean;
}

// カスタムScreenインターフェースを定義
interface CustomScreen extends Omit<Screen, 'orientation'> {
  orientation?: {
    type: string;
    angle: number;
    lock?: (orientation: string) => Promise<void>;
    unlock?: () => void;
  };
  lockOrientation?: (orientation: string) => void;
  unlockOrientation?: () => void;
  mozLockOrientation?: (orientation: string) => void;
  mozUnlockOrientation?: () => void;
  msLockOrientation?: (orientation: string) => void;
  msUnlockOrientation?: () => void;
}

// Documentオブジェクトの拡張型を定義
interface DocumentWithFullscreen extends Document {
  webkitFullscreenElement?: Element | null;
  mozFullScreenElement?: Element | null;
  msFullscreenElement?: Element | null;
}

/**
 * VideoDetailPlayerSectionコンポーネントのロジックを扱うカスタムフック
 */
export const useVideoDetailPlayerSection = ({
  videoPath,
  videoId,
  useStreaming = true,
  initialPosition = 0,
  playerRef,
  onReady,
  onDuration,
  onProgress,
  onEnded,
  autoPlay = true,
  startMuted = false,
  onAutoPlayError
}: VideoDetailPlayerSectionProps): UseVideoDetailPlayerSectionReturn => {
  const [isPortrait, setIsPortrait] = useState(false);
  const [containerWidth, setContainerWidth] = useState('100%');
  const [error, setError] = useState<Error | null>(null);
  const initialSeekDoneRef = useRef(false);
  const [loadRetries, setLoadRetries] = useState(0);
  const retryTimerRef = useRef<NodeJS.Timeout | null>(null);
  
  // 自動再生が可能かどうかを判定するための状態
  const [canAutoPlay, setCanAutoPlay] = useState(true);
  
  // 実際に使用するミュート状態
  const [actualMuted, setActualMuted] = useState(startMuted);
  
  // 初回マウント時の音声再生試行フラグ
  const hasTriedAudioPlayRef = useRef(false);
  
  // 全画面表示の状態を管理
  const [isFullScreen, setIsFullScreen] = useState(false);
  
  // スタイル要素の参照を保持
  const styleElementRef = useRef<HTMLStyleElement | null>(null);
  
  // グローバルCSSを追加するためのスタイル要素を作成
  useEffect(() => {
    // テスト環境やSSRでdocumentが存在しない場合のガード
    if (typeof document === 'undefined') return;
    
    try {
      const styleElement = document.createElement('style');
      styleElement.setAttribute('data-component', 'video-detail-player-section');
      styleElement.textContent = `
        @keyframes fadeInDown {
          from {
            opacity: 0;
            transform: translate(-50%, -20px);
          }
          to {
            opacity: 1;
            transform: translate(-50%, 0);
          }
        }
      `;
      
      if (document.head) {
        document.head.appendChild(styleElement);
        styleElementRef.current = styleElement;
      }
    } catch (error) {
      console.warn('Failed to create style element:', error);
    }
    
    return () => {
      // クリーンアップ時に作成したスタイル要素を削除
      if (styleElementRef.current && document.head && document.head.contains(styleElementRef.current)) {
        try {
          document.head.removeChild(styleElementRef.current);
          styleElementRef.current = null;
        } catch (error) {
          console.warn('Failed to remove style element:', error);
        }
      }
    };
  }, []);
  
  // 全画面表示の変更を検知するイベントリスナーを設定
  useEffect(() => {
    // テスト環境やSSRでdocumentが存在しない場合のガード
    if (typeof document === 'undefined') return;
    
    const handleFullscreenChange = () => {
      const doc = document as DocumentWithFullscreen;
      const isCurrentlyFullScreen = !!(
        doc.fullscreenElement || 
        doc.webkitFullscreenElement || 
        doc.mozFullScreenElement || 
        doc.msFullscreenElement
      );
      
      setIsFullScreen(isCurrentlyFullScreen);
      
      // 全画面状態の管理のみ行う（画面向きロックは削除）
    };
    
    // 各ブラウザ向けのフルスクリーン変更イベントを監視
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', handleFullscreenChange);
    document.addEventListener('MSFullscreenChange', handleFullscreenChange);
    
    return () => {
      // クリーンアップ時にイベントリスナーを削除
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
      document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
      
      // クリーンアップ完了
    };
  }, []);
  
  
  // アニメーションのためのCSSを追加
  useEffect(() => {
    // テスト環境やSSRでdocumentが存在しない場合のガード
    if (typeof document === 'undefined') return;
    
    const styleId = 'video-detail-player-section-custom-audio-control';
    
    // 既存のスタイルがある場合は何もしない
    if (document.getElementById(styleId)) return;
    
    try {
      const styleElement = document.createElement('style');
      styleElement.id = styleId;
      styleElement.textContent = `
        .video-detail-player-section__custom-audio-control {
          position: absolute;
          bottom: 50px;
          right: 15px;
          background-color: rgba(0, 0, 0, 0.6);
          color: white;
          border: 1px solid rgba(255, 255, 255, 0.2);
          border-radius: 4px;
          padding: 4px 8px;
          font-size: 12px;
          cursor: pointer;
          z-index: 5;
          display: flex;
          align-items: center;
          gap: 4px;
          transition: all 0.2s ease;
          opacity: 0.8;
        }
        
        .video-detail-player-section__react-player-container:hover .video-detail-player-section__custom-audio-control {
          opacity: 1;
        }
        
        .video-detail-player-section__custom-audio-control:hover {
          background-color: rgba(0, 0, 0, 0.8);
          transform: scale(1.05);
        }
        
        .video-detail-player-section__custom-audio-control .video-detail-player-section__sound-icon {
          font-size: 14px;
        }
        
        @media (max-width: 650px) {
          .video-detail-player-section__custom-audio-control {
            bottom: 45px;
            right: 10px;
            padding: 3px 6px;
            font-size: 11px;
          }
        }
      `;
      
      if (document.head) {
        document.head.appendChild(styleElement);
      }
    } catch (error) {
      console.warn('Failed to create custom audio control styles:', error);
    }
    
    return () => {
      // クリーンアップ時にスタイルを削除
      const existingStyle = document.getElementById(styleId);
      if (existingStyle && document.head && document.head.contains(existingStyle)) {
        try {
          document.head.removeChild(existingStyle);
        } catch (error) {
          console.warn('Failed to remove custom audio control styles:', error);
        }
      }
    };
  }, []);
  
  // 安全にストリーミングURLを取得する関数
  const getSafeStreamingUrl = (id: string): string => {
    try {
      return id ? getVideoStreamUrl(id) : '';
    } catch (error) {
      console.error('ストリーミングURL取得エラー:', error);
      return '';
    }
  };

  // 実際に使用するURLを決定
  // useStreamingがtrueかつvideoIdが提供されている場合はストリーミングAPIを使用、そうでない場合は従来のパスを使用
  const [videoUrl, setVideoUrl] = useState<string>(
    useStreaming && videoId ? getSafeStreamingUrl(videoId) : (videoPath || '')
  );
  
  // videoIdやuseStreamingが変更されたときにURLを更新
  useEffect(() => {
    const streamingUrl = useStreaming && videoId 
      ? getSafeStreamingUrl(videoId) 
      : videoPath || '';
    
    setVideoUrl(streamingUrl);
    setLoadRetries(0);
    if (retryTimerRef.current) {
      clearTimeout(retryTimerRef.current);
      retryTimerRef.current = null;
    }
  }, [videoId, videoPath, useStreaming]);

  // コンポーネントがマウントされたらシーク状態をリセット
  useEffect(() => {
    return () => {
      initialSeekDoneRef.current = false;
    };
  }, [videoUrl, videoId, videoPath, useStreaming, initialPosition]);

  // リトライタイマーをクリアする関数
  useEffect(() => {
    return () => {
      if (retryTimerRef.current) {
        clearTimeout(retryTimerRef.current);
        retryTimerRef.current = null;
      }
    };
  }, []);

  // ResizeObserver関連のエラーを抑制するための対策
  useEffect(() => {
    // ブラウザ環境でない場合は何もしない
    if (typeof window === 'undefined') return;
    
    // ResizeObserverループエラーを抑制するための処理
    const resizeObserverError = (error: ErrorEvent) => {
      // エラーとそのメッセージが存在することを確認
      if (error && error.message) {
        if (error.message.includes('ResizeObserver') || 
            error.message.includes('ResizeObserver loop completed with undelivered notifications')) {
          // エラーをキャンセルして、コンソールにはログだけ残す
          error.stopImmediatePropagation();
          console.warn('ResizeObserver error suppressed in video player:', error.message);
        }
      }
    };
    
    // イベントリスナーを追加
    window.addEventListener('error', resizeObserverError, true);
    
    // クリーンアップ関数
    return () => {
      window.removeEventListener('error', resizeObserverError, true);
    };
  }, []);

  // サイドバーの幅（ピクセル）- モバイルブレークポイント以上で表示される想定
  const SIDEBAR_WIDTH = 150; // サイドバーの実際の幅に合わせて調整（130px→150pxに変更）
  // 画面幅のブレークポイント
  const SIDEBAR_BREAKPOINT = BREAKPOINTS.MOBILE;

  // ウィンドウサイズの変更を監視
  useEffect(() => {
    // ブラウザ環境でない場合は何もしない
    if (typeof window === 'undefined') return;
    
    const handleResize = () => {
      // 画面幅に基づいてコンテナサイズを調整
      updateContainerSize();
    };

    // 初期サイズを設定
    updateContainerSize();

    // リサイズイベントリスナーを追加
    window.addEventListener('resize', handleResize);
    
    // クリーンアップ関数
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // コンテナサイズを画面サイズに合わせて更新
  const updateContainerSize = () => {
    // ブラウザ環境でない場合はデフォルトを使用
    if (typeof window === 'undefined') {
      setContainerWidth('100%');
      return;
    }
    
    const viewportWidth = window.innerWidth;
    
    // モバイル表示（ブレークポイント以下）の場合は画面幅いっぱいに設定
    if (viewportWidth < SIDEBAR_BREAKPOINT) {
      setContainerWidth('100%');
      return;
    }
    
    // PC表示（ブレークポイント以上）の場合はサイドバーの幅を考慮
    const availableWidth = viewportWidth - SIDEBAR_WIDTH;
    
    // 利用可能な幅の95%を使用（余白を残す）
    const maxPlayerWidth = Math.min(availableWidth * 0.95, 1200); // 最大1200pxまで
    setContainerWidth(`${maxPlayerWidth}px`);
  };

  // 動画がロードされたときにアスペクト比を確認する
  const handleVideoReady = (player: ReactPlayerInstance) => {
    // 初期再生位置が指定されており、まだシークが行われていない場合のみシーク
    if (initialPosition > 0 && !initialSeekDoneRef.current && player) {
      console.warn(`前回の視聴位置からの再生を試みます: ${initialPosition}秒`);
      try {
        if (player.seekTo) {
          player.seekTo(initialPosition);
        }
        console.warn(`シーク完了: ${initialPosition}秒に移動しました`);
        initialSeekDoneRef.current = true;
      } catch (seekError) {
        console.error('前回位置へのシークに失敗:', seekError);
        initialSeekDoneRef.current = true;
      }
    } else {
      console.warn(`初期位置設定なし: initialPosition=${initialPosition}, シーク済み=${initialSeekDoneRef.current}`);
    }
    
    if (onReady) onReady(player);
    
    // 動画のメタデータからアスペクト比を取得
    let internalPlayer = null;
    try {
      internalPlayer = player && typeof player.getInternalPlayer === 'function' ? player.getInternalPlayer() : null;
    } catch (error) {
      console.warn('getInternalPlayer failed:', error);
    }
    const video = internalPlayer && internalPlayer.vjsPlayer ? internalPlayer.vjsPlayer.el()?.querySelector('video') : null;
    if (video && video.videoWidth && video.videoHeight) {
      const ratio = video.videoWidth / video.videoHeight;
      
      // 縦長動画の場合
      if (ratio < 1) {
        setIsPortrait(true);
      } else {
        setIsPortrait(false);
      }
      
      // 初回ロード時にもコンテナサイズを更新
      updateContainerSize();
    }
    
    // エラーとリトライカウンターをリセット
    setError(null);
    setLoadRetries(0);
  };

  // プレーヤーからのボリューム変更を検知するための関数
  const handleReady = (player: ReactPlayerInstance) => {
    // playerRefが提供されている場合は設定
    if (playerRef) {
      playerRef.current = player;
    }

    // 音声付き再生を試みる処理
    if (player && autoPlay && !hasTriedAudioPlayRef.current) {
      hasTriedAudioPlayRef.current = true; // 一度のみ実行
      
      let internalPlayer = null;
      try {
        internalPlayer = typeof player.getInternalPlayer === 'function' ? player.getInternalPlayer() : null;
      } catch (error) {
        console.warn('getInternalPlayer failed in handleReady:', error);
      }
      // videoElement.play() は VHS/MSE セットアップ前に呼ぶと "no supported sources"
      // になるため、VideoJS の API 経由で再生する
      // ラッパー実装によっては vjsPlayer が muted/play を持たない形状もあるためガードする
      const candidate = (internalPlayer as { vjsPlayer?: Partial<VjsPlayerLike> } | null)?.vjsPlayer;
      const vjsPlayer =
        candidate && typeof candidate.muted === 'function' && typeof candidate.play === 'function'
          ? (candidate as VjsPlayerLike)
          : null;

      if (vjsPlayer) {
        vjsPlayer.muted(false);
        setActualMuted(false);

        const playPromise = vjsPlayer.play();
        if (playPromise !== undefined) {
          playPromise.catch((error: unknown) => {
            // 読み込み中に関連動画へ移ると、プレイヤーが dispose されたあとでこの reject が届く。
            // dispose 済みの Player#muted(true) は video.js 内部で TypeError を投げ、
            // 未処理の reject としてエラー画面に移ってしまう。自動再生の拒否でもないので、
            // ミュートへの切り替えも通知もせずに終える
            if (typeof vjsPlayer.isDisposed === 'function' && vjsPlayer.isDisposed()) {
              return;
            }
            const err = error instanceof Error ? error : new Error(String(error));

            if (onAutoPlayError && typeof onAutoPlayError === 'function') {
              onAutoPlayError(err);
            }

            vjsPlayer.muted(true);
            setActualMuted(true);
            setCanAutoPlay(false);

            const mutedPlayPromise = vjsPlayer.play();
            if (mutedPlayPromise !== undefined) {
              mutedPlayPromise.catch(() => {
                // ミュートでも再生できない場合は諦める（ユーザー操作待ち）
              });
            }
          });
        }
      }
    }
    
    // 通常のhandleVideoReadyを呼び出し
    handleVideoReady(player);
  };

  // エラーハンドリングを改善
  const handleError = (e: unknown) => {
    console.error('ビデオプレイヤーエラー:', e);

    const errObj = (e && typeof e === 'object') ? e as Record<string, unknown> : null;
    const errName = errObj && typeof errObj.name === 'string' ? errObj.name : '';
    const errMessage = errObj && typeof errObj.message === 'string' ? errObj.message : '';
    const isRecoverable = errObj && typeof errObj.recoverable === 'boolean' ? errObj.recoverable : undefined;

    // useVideoErrorHandler がまだ回復を試行中（recoverable: true）の場合は
    // このレイヤーでのリトライをスキップし、回復を待つ
    if (isRecoverable === true) {
      return;
    }

    // 自動再生に関するエラーかどうかをチェック
    if (errName === 'NotAllowedError') {
      console.warn('自動再生が許可されていません。ミュートしてリトライします。');
      setCanAutoPlay(false);
      setActualMuted(true);

      // コールバックがあれば呼び出す
      if (onAutoPlayError && typeof onAutoPlayError === 'function') {
        onAutoPlayError(e instanceof Error ? e : new Error(String(e)));
      }

      // エラーメッセージを設定しない (自動回復を試みるため)
      return;
    }

    // 音声トラックがない可能性をチェック
    if (errMessage.includes('no supported stream found') ||
        errMessage.includes('audio track') ||
        errMessage.includes('Media element has no supported audio')) {

      console.warn('音声トラックがない可能性があります。動画のみ再生を試みます。');

      // 音声不要フラグをtrueに設定し、再読込を試みる
      if (playerRef && playerRef.current) {
        let internalPlayer = null;
        try {
          internalPlayer = typeof playerRef.current.getInternalPlayer === 'function' ? playerRef.current.getInternalPlayer() : null;
        } catch (error) {
          console.warn('getInternalPlayer failed in handleError:', error);
        }
        const videoElement = internalPlayer && internalPlayer.vjsPlayer ? internalPlayer.vjsPlayer.el()?.querySelector('video') : null;
        if (videoElement) {
          // 再生を続行
          if (videoElement.paused) {
            videoElement.play().catch((innerError: unknown) => {
              console.error('音声なしでも再生に失敗:', innerError);
              setError(new Error('音声なしでも再生に失敗しました。動画ファイルに問題がある可能性があります。'));
            });
          }

          // エラーを設定しない
          return;
        }
      }
    }

    // その他のエラー処理（recoverable: false または useVideoErrorHandler 以外からのエラー）
    const errorMessage = errMessage || '不明なエラー';
    console.error('動画再生に失敗しました。詳細情報: ', {
      videoId,
      videoPath,
      currentUrl: videoUrl,
      retryCount: loadRetries,
      error: errorMessage
    });

    // ストリーミングAPI使用中で、useVideoErrorHandlerでの回復が全て失敗した場合の最終手段リトライ
    // maxRetriesを1に削減（URL再生成は大量ダウンロードを伴うため最終手段）
    if (useStreaming && videoId && loadRetries < FINAL_RETRY_MAX) {
      const nextRetryCount = loadRetries + 1;
      console.warn(`ストリーミングURL再生成による最終リトライ (${nextRetryCount}/${FINAL_RETRY_MAX})`);

      // リトライ回数をインクリメント
      setLoadRetries(nextRetryCount);

      // リトライタイマーをクリア
      if (retryTimerRef.current) {
        clearTimeout(retryTimerRef.current);
      }

      // 最終手段なので長めの待機（5秒）
      retryTimerRef.current = setTimeout(() => {
        const newStreamingUrl = getSafeStreamingUrl(videoId);
        if (newStreamingUrl) {
          console.warn('ストリーミングURLを再生成してリトライ（最終手段）');
          setVideoUrl(newStreamingUrl);
          setError(null);
        } else {
          console.error('ストリーミングURLの再生成に失敗しました');
          setError(new Error('ストリーミングURLの取得に失敗しました'));
        }
      }, 5000);
    } else {
      if (loadRetries >= FINAL_RETRY_MAX) {
        console.error(`最終リトライ回数(${FINAL_RETRY_MAX})に達しました`);
      }
      setError(new Error(errorMessage));
    }
  };

  // スタイル定義
  const styles = {
    playerWrapper: {
      position: 'relative',
      backgroundColor: '#000',
      borderRadius: '8px',
      overflow: 'hidden',
      // アスペクト比は動画の実際の比率に従う
      margin: '0 auto', // 中央揃え
    } as React.CSSProperties,
    landscapeContainer: {
      width: '100%',
      height: '100%',
    } as React.CSSProperties,
    portraitContainer: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '100%',
      width: '100%',
    } as React.CSSProperties,
    errorContainer: {
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      color: 'white',
      backgroundColor: 'rgba(0, 0, 0, 0.7)',
      padding: '20px',
      borderRadius: '5px',
      textAlign: 'center',
      zIndex: 10
    } as React.CSSProperties,
    loadingContainer: {
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      color: 'white',
      textAlign: 'center',
      zIndex: 10
    } as React.CSSProperties,
    // ミュート通知のスタイル
    muteNotification: {
      position: 'absolute',
      top: '20px',
      left: '50%',
      transform: 'translateX(-50%)',
      zIndex: 10,
      padding: '10px 15px',
      backgroundColor: 'rgba(0, 0, 0, 0.75)',
      borderRadius: '30px',
      boxShadow: '0 4px 8px rgba(0, 0, 0, 0.3)',
      transition: 'opacity 0.5s ease, transform 0.5s ease',
      animation: 'fadeInDown 0.5s ease forwards',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    } as React.CSSProperties,
    unmuteButton: {
      background: 'rgba(255, 255, 255, 0.15)',
      border: '1px solid rgba(255, 255, 255, 0.3)',
      color: 'white',
      padding: '8px 15px',
      cursor: 'pointer',
      borderRadius: '20px',
      fontSize: '14px',
      fontWeight: 'bold',
      display: 'flex',
      alignItems: 'center',
      gap: '5px',
      transition: 'background 0.2s ease, transform 0.1s ease',
      backdropFilter: 'blur(5px)',
      WebkitBackdropFilter: 'blur(5px)'
    } as React.CSSProperties,
    soundIcon: {
      fontSize: '16px',
      marginRight: '5px'
    } as React.CSSProperties,
  };

  return {
    error,
    isPortrait,
    containerWidth,
    actualMuted,
    isFullScreen,
    videoUrl,
    handleReady,
    handleError,
    styles,
  };
}; 
